import { getAllPatients } from '../constants/mockPatients';
import { resolvePatientFromHints } from './patientMatch';
import { isProgressNotePlaceholder } from './progressNoteBody';
import { stripHandoverPlaceholders } from './handoverNoteBody';

function flagText(flag) {
  if (typeof flag === 'string') return flag;
  return flag?.message || flag?.text || flag?.value || '';
}

function isPatientVerificationFlag(flag) {
  const text = flagText(flag).toLowerCase();
  if (!text) return false;
  return (
    text.includes('patient identifier missing') ||
    text.includes('no name or room/bed provided') ||
    text.includes('no name or room provided') ||
    text.includes('patient details not provided') ||
    text.includes('patient name not detected') ||
    text.includes('room number not detected') ||
    text.includes('patient name or room number not detected') ||
    text.includes('patient name not identified') ||
    text.includes('room not identified')
  );
}

function stripPatientVerificationFlags(flags = []) {
  return flags.filter((flag) => !isPatientVerificationFlag(flag));
}

function ensureArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function narrativeFromSbar(sbar = {}) {
  return [sbar.situation, sbar.background, sbar.assessment, sbar.recommendation]
    .map((value) => String(value || '').trim())
    .filter((value) => value && !isProgressNotePlaceholder(value))
    .join('\n\n');
}

export function normalizeHandoverPatientEntry(entry = {}) {
  const fromNarrative =
    typeof entry.narrative === 'string' ? stripHandoverPlaceholders(entry.narrative) : '';
  const narrative = fromNarrative || narrativeFromSbar(entry.sbar);

  return {
    patient_name: entry.patient_name ?? entry.patient?.name ?? null,
    room: entry.room ?? entry.patient?.room ?? null,
    narrative,
    sbar: {
      situation: narrative || '',
      background: '',
      assessment: '',
      recommendation: '',
    },
    outstanding_tasks: ensureArray(entry.outstanding_tasks),
    negations_detected: ensureArray(entry.negations_detected),
    flags: stripPatientVerificationFlags(ensureArray(entry.flags)),
  };
}

export function normalizeHandoverNote(note) {
  if (!note) return null;

  const normalized = {
    ...note,
    note_type: 'handover',
    flags: ensureArray(note.flags),
  };

  if (normalized.patients?.length) {
    normalized.patients = normalized.patients.map(normalizeHandoverPatientEntry);
  } else {
    normalized.patients = [
      normalizeHandoverPatientEntry({
        patient_name: note.patient?.name ?? null,
        room: note.patient?.room ?? null,
        narrative: note.narrative,
        sbar: note.sbar,
        outstanding_tasks: note.outstanding_tasks,
        negations_detected: note.negations_detected,
        flags: note.flags,
      }),
    ];
  }

  // Keep top-level narrative/sbar in sync with cleaned patient bodies (no "Not documented").
  if (normalized.patients.length === 1) {
    const body = normalized.patients[0].narrative || '';
    normalized.narrative = body;
    normalized.sbar = {
      situation: body,
      background: '',
      assessment: '',
      recommendation: '',
    };
  } else if (typeof normalized.narrative === 'string') {
    normalized.narrative = stripHandoverPlaceholders(normalized.narrative);
  }

  return normalized;
}

export function getHandoverPatients(note) {
  const normalized = normalizeHandoverNote(note);
  return normalized?.patients || [];
}

export function isMultiPatientHandover(note) {
  return getHandoverPatients(note).length > 1;
}

export function toSinglePatientHandoverNote(parentNote, patientEntry) {
  const entry = normalizeHandoverPatientEntry(patientEntry);
  return {
    note_type: 'handover',
    datetime: parentNote?.datetime || new Date().toISOString(),
    patient: {
      name: entry.patient_name,
      room: entry.room,
      age: null,
      sex: null,
      details_source: 'transcript',
    },
    narrative: entry.narrative || '',
    sbar: entry.sbar,
    outstanding_tasks: entry.outstanding_tasks,
    negations_detected: entry.negations_detected,
    flags: entry.flags,
  };
}

export function enrichHandoverPatientsFromRoster(note, { admittedPatients = [], transcript } = {}) {
  const normalized = normalizeHandoverNote(note);
  const roster = getAllPatients(admittedPatients);

  normalized.patients = normalized.patients.map((entry) => {
    const resolved = resolvePatientFromHints({
      patients: roster,
      transcript,
      patientHints: { name: entry.patient_name, room: entry.room },
    });
    if (!resolved?.patient) return entry;

    return {
      ...entry,
      patient_name: resolved.patient.nameEn || resolved.patient.name || entry.patient_name,
      room: resolved.patient.room || entry.room,
      flags: stripPatientVerificationFlags(entry.flags || []),
    };
  });

  return normalized;
}

export function applyKnownPatientToHandoverNote(note, patientContext) {
  if (!note || !patientContext) return note;

  const normalized = normalizeHandoverNote(note);
  if (normalized.patients.length !== 1) return normalized;

  normalized.patients[0] = {
    ...normalized.patients[0],
    patient_name: patientContext.name || normalized.patients[0].patient_name,
    room: patientContext.room || normalized.patients[0].room,
    flags: stripPatientVerificationFlags(normalized.patients[0].flags || []),
  };

  return normalized;
}
